import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import LeftToEarnBox from "./LeftToEarnBox";
import PrizeWheelVersionTwo from "./PrizeWheelVersionTwo";
import ProgressBar from "./ProgressBar";
import QuotesSalesProgressBox from "./QuotesSalesProgressBox";
import SelectedCapSpif from "./SelectedCapSpif";
import SelectedOneToOneSpif from "./SelectedOneToOneSpif";
import * as incentiveTypes from "../utils/constants/incentiveTypes";
import confetti from "../images/Confetti.svg";
import trophy from "../images/Trophy.svg";
import "../styles/SelectedPromotion.css";

const SelectedPromotion = ({ incentiveName, type, ...props }) => {
  const navigate = useNavigate();
  const [isSpifOneToOne, setIsSpifOneToOne] = useState(false);
  const [isSpifCap, setIsSpifCap] = useState(false);
  const [isSpifCompleted, setIsSpifCompleted] = useState(false);
  const [currentSvg, setCurrentSvg] = useState(null);
  const [confettiKey, setConfettiKey] = useState(0);

  const routeToEarnings = () => {
    navigate("/earnings", { state: { editMode: true } });
  };

  const getDaysLeftChipClass = () => {
    return props.daysLeftBeforeEndDate <= 5
      ? "chips chips-warning"
      : "chips chips-success";
  };

  const getTokensLeftChipClass = () => {
    return props.numberOfSpins <= 0
      ? "chips chips-neutral"
      : "chips chips-tokens-left";
  };

  useEffect(() => {
  setIsSpifCompleted(props.spifProgress >= props.spifGoal);
  }, [props.spifProgress, props.spifGoal]);

  useEffect(() => {
    setCurrentSvg(trophy);
    setConfettiKey((prevKey) => prevKey + 1);
  }, []);

  useEffect(() => {
    setIsSpifOneToOne(
      props.spifGoal === 1 &&
        props.updatedOneToOneSpifFlag &&
        (props.maxNumberOfPayOuts === null || props.maxNumberOfPayOuts <= 1)
    );
  }, [props.spifGoal, props.updatedOneToOneSpifFlag, props.maxNumberOfPayOuts]);

  useEffect(() => {
    setIsSpifCap(props.maxNumberOfPayOuts > 1);
  }, [props.maxNumberOfPayOuts]);

  return (
    <div className="selected-promotion-container">
      <div className="selected-promotion-header">
        <h4>{incentiveName}</h4>
        <div className="header-chips">
          <div className={getDaysLeftChipClass()}>
            {props.daysLeftBeforeEndDate.toLocaleString()} days left
          </div>
          {type === incentiveTypes.Gamification && (
            <div className={getTokensLeftChipClass()}>
              {props.numberOfSpins.toLocaleString()} tokens left
            </div>
          )}
        </div>
      </div>
      {type === incentiveTypes.Spif && (
        <div className="selected-promotion-body">
          <p className="description-text">
            {props.promotionType === "Quotes" ? "Quote " : "Sell policies "}
            to earn prizes.
            <br />
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a
              href="#"
              data-trackid="learn-more-promotion"
              onClick={(event) => {
                event.preventDefault();
                props.toggle();
              }}
            >
              Learn more about this promotion.
            </a>
          </p>
          {isSpifOneToOne && !isSpifCap && (
            <SelectedOneToOneSpif
              iteration={props.spifIteration}
              payout={props.nextPayout?.toLocaleString()}
            />
          )}
          {isSpifCap && (
            <SelectedCapSpif
              promotionType={props.promotionType}
              iteration={props.spifIteration}
              nextPayout={props.nextPayout}
              maxNumberOfPayOuts={props.maxNumberOfPayOuts}
            />
          )}
          {!isSpifOneToOne && !isSpifCap && (
            <>
              {isSpifCompleted ? (
                <div
                  className="celebration-container-other-spif"
                  data-testid="celebration-container"
                >
                  {currentSvg && (
                    <div>
                      <img
                        src={currentSvg}
                        alt="Trophy svg"
                        className="trophy-svg"
                      />
                      <img
                        src={confetti}
                        alt="Confetti svg"
                        className="confetti-svg-other-spif"
                        key={confettiKey}
                      />
                    </div>
                  )}
                  <div className="yes-progress-text">Congrats!</div>
                  <div className="payout-background-other-spif">
                    <div className="payout-text-other-spif">
                      You've completed the promotion and earned{" "}
                      <span className="payout-amount-other-spif">
                        ${parseFloat(props.nextPayout).toLocaleString()}
                      </span>.
                    </div>
                  </div>
                  <div className="earnings-link-container-other-spif">
                    <button
                      onClick={routeToEarnings}
                      className="earnings-link-other-spif"
                      type="text"
                      data-trackid="view-promotion-earnings"
                    >
                      View My Earnings
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="progress-boxes-container-other-spif">
                    <QuotesSalesProgressBox
                      promotionType={props.promotionType}
                      progress={props.spifProgress}
                      cap={props.spifGoal}
                    />
                    <LeftToEarnBox
                      promotionType={props.promotionType}
                      progress={props.spifProgress}
                      cap={props.spifGoal}
                      payout={props.nextPayout?.toLocaleString()}
                    />
                  </div>
                  <ProgressBar
                    goal={props.spifGoal}
                    progress={props.spifProgress}
                    payout={props.nextPayout?.toLocaleString()}
                    promotionType={props.promotionType}
                  />
                </>
              )}
            </>
          )}
        </div>
      )}
      {type === incentiveTypes.Gamification && (
        <div className="selected-promotion-body">
          <PrizeWheelVersionTwo
            chosenIncentiveId={props.chosenIncentiveId}
            numberOfSpins={props.numberOfSpins}
            payoutOptions={props.payoutOptions}
            prizeWheelSoundFlag={props.prizeWheelSoundFlag}
            getPayoutToken={props.getPayoutToken}
            updateSpinsForIncentive={props.updateSpinsForIncentive}
            updatePromotionEarnings={props.updatePromotionEarnings}
            toggle={props.toggle}
          />
        </div>
      )}
    </div>
  );
};

SelectedPromotion.propTypes = {
  incentiveName: PropTypes.string,
  type: PropTypes.string,
  promotionType: PropTypes.string,
  toggle: PropTypes.func,
  daysLeftBeforeEndDate: PropTypes.number,
  spifGoal: PropTypes.number,
  spifProgress: PropTypes.number,
  nextPayout: PropTypes.number,
  chosenIncentiveId: PropTypes.string,
  numberOfSpins: PropTypes.number,
  payoutOptions: PropTypes.array,
  prizeWheelSoundFlag: PropTypes.bool,
  getPayoutToken: PropTypes.func,
  updateSpinsForIncentive: PropTypes.func,
  updatePromotionEarnings: PropTypes.func,
  updatedOneToOneSpifFlag: PropTypes.bool,
  spifIteration: PropTypes.number,
  maxNumberOfPayOuts: PropTypes.number,
};

export default SelectedPromotion;
